import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/* ── Helpers ──────────────────────────────────────────────────────────── */
function fmtS(n) {
  return `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// paid_at es TIMESTAMP real (instante UTC) — se proyecta a Perú explícitamente
// (getDate()/getMonth() usarían la zona del proceso/navegador, no Perú).
function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Lima' });
}

function fmtNow() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fileDateStr() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`;
}

// Cada cobranza (cabecera) trae sus ventas anidadas en `.ventas` — se
// aplana a una fila por venta pagada (igual detalle que antes, cuando
// cada ar_payment era su propia fila suelta).
function buildRows(data) {
  const out = [];
  let n = 0;
  data.forEach(co => {
    const ventas = (co.ventas && co.ventas.length) ? co.ventas : [{ sale_number: null, cuota_num: null, amount: co.amount }];
    ventas.forEach(v => {
      n += 1;
      out.push({
        num:      n,
        fecha:    fmtDate(co.paid_at),
        cliente:  co.client_name || '—',
        doc:      co.client_document || '—',
        venta:    v.sale_number || '—',
        cuota:    v.cuota_num ? `Cuota ${v.cuota_num}` : '—',
        forma:    co.payment_method_short || co.payment_method_name || '—',
        monto:    parseFloat(v.amount || 0),
        usuario:  co.username || '—',
      });
    });
  });
  return out;
}

/* ──────────────────────────────────────────────────────────────────────
   EXPORTAR PDF
─────────────────────────────────────────────────────────────────────── */
export function exportPDF({ empresa, data, usuario, totalCobrado }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W    = doc.internal.pageSize.getWidth();
  const navy = [13, 27, 46];
  const accent = [14, 165, 233];
  const gray = [100, 116, 139];
  const green = [22, 163, 74];
  const now  = fmtNow();

  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 22, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text(empresa.name || 'PIVASAPOLLOS', 12, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(180, 210, 230);
  doc.text(`RUC: ${empresa.ruc || '20100000001'}  |  ${empresa.address || ''}`, 12, 16);
  doc.text(`Generado: ${now}  |  Usuario: ${usuario || 'sistema'}`, W - 12, 16, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...navy);
  doc.text('COBRANZAS — HISTORIAL DE COBROS REALIZADOS', W / 2, 30, { align: 'center' });
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.5);
  doc.line(12, 33, W - 12, 33);

  doc.setFillColor(...green);
  doc.roundedRect(12, 37, 70, 10, 2, 2, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);
  doc.text('Total Cobrado (filtro actual)', 47, 40.5, { align: 'center' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(fmtS(totalCobrado || 0), 47, 45, { align: 'center' });

  const rows = buildRows(data);
  const cols = ['#', 'Fecha', 'Cliente', 'Doc.', 'N° Venta', 'Cuota', 'Forma de Pago', 'Monto', 'Usuario'];
  const body = rows.map(r => [r.num, r.fecha, r.cliente, r.doc, r.venta, r.cuota, r.forma, fmtS(r.monto), r.usuario]);

  autoTable(doc, {
    startY: 52,
    head: [cols],
    body,
    theme: 'grid',
    styles: { fontSize: 7.5, cellPadding: { top: 2, right: 2.5, bottom: 2, left: 2.5 }, overflow: 'linebreak', valign: 'middle' },
    headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      7: { halign: 'right', fontStyle: 'bold', textColor: green },
    },
    didDrawPage() {
      const pageCount = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...gray);
      doc.text(`Página ${currentPage} de ${pageCount}  —  PIVASAPOLLOS  —  ${now}`, W / 2, doc.internal.pageSize.getHeight() - 5, { align: 'center' });
    },
  });

  doc.save(`Cobranzas_${fileDateStr()}.pdf`);
}

/* ──────────────────────────────────────────────────────────────────────
   EXPORTAR EXCEL
─────────────────────────────────────────────────────────────────────── */
export function exportExcel({ empresa, data, usuario, totalCobrado }) {
  const wb = XLSX.utils.book_new();
  const ws_data = [];

  const S = {
    title: { font: { bold: true, sz: 15, color: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' } },
    sub:   { font: { bold: true, sz: 11, color: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' } },
    meta:  { font: { sz: 8.5, color: { rgb: '64748B' } }, alignment: { horizontal: 'center' } },
    hdr:   { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center', wrapText: true } },
    def:   { font: { sz: 9 } },
    numR:  { font: { bold: true, sz: 9, color: { rgb: '16A34A' } }, alignment: { horizontal: 'right' } },
    center:{ font: { sz: 9 }, alignment: { horizontal: 'center' } },
  };

  const cell = (v, s) => ({ v, t: typeof v === 'number' ? 'n' : 's', s });
  const NCOLS = 9;

  ws_data.push([cell(empresa.name || 'PIVASAPOLLOS', S.title)]);
  ws_data.push([cell(`RUC: ${empresa.ruc || '20100000001'}  |  ${empresa.address || ''}`, S.meta)]);
  ws_data.push([]);
  ws_data.push([cell('COBRANZAS — HISTORIAL DE COBROS REALIZADOS', S.sub)]);
  ws_data.push([cell(`Generado: ${fmtNow()}  |  Usuario: ${usuario || 'sistema'}  |  Total Cobrado: ${fmtS(totalCobrado || 0)}`, { font: { sz: 8, italic: true, color: { rgb: '94A3B8' } }, alignment: { horizontal: 'center' } })]);
  ws_data.push([]);

  ws_data.push([
    cell('#', S.hdr), cell('Fecha', S.hdr), cell('Cliente', S.hdr), cell('Doc.', S.hdr),
    cell('N° Venta', S.hdr), cell('Cuota', S.hdr), cell('Forma de Pago', S.hdr),
    cell('Monto', S.hdr), cell('Usuario', S.hdr),
  ]);

  const rows = buildRows(data);
  rows.forEach(r => {
    ws_data.push([
      cell(r.num, S.center), cell(r.fecha, S.center), cell(r.cliente, { font: { bold: true, sz: 9 } }),
      cell(r.doc, S.center), cell(r.venta, { font: { bold: true, sz: 9, color: { rgb: '0369A1' } } }),
      cell(r.cuota, S.center), cell(r.forma, S.center), cell(r.monto, S.numR), cell(r.usuario, S.def),
    ]);
  });

  const totalMonto = rows.reduce((s, r) => s + r.monto, 0);
  ws_data.push([
    cell('', {}), cell('', {}), cell('', {}), cell('', {}), cell('', {}), cell('', {}),
    cell('TOTAL', { font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' } }),
    cell(totalMonto, { font: { bold: true, sz: 9, color: { rgb: '16A34A' } }, alignment: { horizontal: 'right' }, fill: { fgColor: { rgb: 'EFF6FF' } } }),
    cell('', {}),
  ]);

  const ws = XLSX.utils.aoa_to_sheet(ws_data, { skipHeader: true });
  ws['!cols'] = [
    { wpx: 35 }, { wpx: 80 }, { wpx: 160 }, { wpx: 95 },
    { wpx: 100 }, { wpx: 70 }, { wpx: 110 }, { wpx: 80 }, { wpx: 100 },
  ];
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: NCOLS - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: NCOLS - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: NCOLS - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: NCOLS - 1 } },
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Cobranzas');
  XLSX.writeFile(wb, `Cobranzas_${fileDateStr()}.xlsx`);
}
